import { stringOrDate, View, CalendarProps, Event } from 'react-big-calendar'
import { withDragAndDropProps } from 'react-big-calendar/lib/addons/dragAndDrop'
import { Color } from '@ocean/theme'

export type Resource = {
  // String because validation on falsy into 'react-big-calendar'
  resourceId: string
  resourceTitle: string
}

export type BaseEvent = Event & {
  id: string
  // String because validation on falsy into 'react-big-calendar'
  resourceId: string
  inactive?: boolean
  currentBooking?: boolean
  title?: string
}

export enum EventStatus {
  Ongoing,
  Upcoming,
  Past,
}

export type ReferenceRecord = {
  title: string
  color: Color
  pattern?: boolean
}

type PlannerCalendarProps<
  TEvent extends object = BaseEvent,
  TResource extends object = object
> = Pick<
  CalendarProps<TEvent, TResource>,
  | 'events'
  | 'resources'
  | 'resourceIdAccessor'
  | 'resourceTitleAccessor'
  | 'defaultDate'
  | 'onSelectSlot'
  | 'onView'
  | 'selectable'
  | 'onSelectEvent'
  | 'getNow'
  | 'onRangeChange'
>

type PlannerCalendarDragAndDropProps<
  TEvent extends object = BaseEvent,
  TResource extends object = object
> = Pick<
  withDragAndDropProps<TEvent, TResource>,
  'onEventDrop' | 'onEventResize' | 'draggableAccessor'
>

type PlannerCustomProps = {
  shutdownDurationGap?: number // The disabled gap in minutes after the event
  launchDurationGap?: number // The disabled gap in minutes before the event
  onRefresh?: () => void
  dataTest?: string
}

export type PlannerProps<
  TEvent extends object = BaseEvent,
  TResource extends object = object
> = PlannerCalendarProps<TEvent, TResource> &
  PlannerCalendarDragAndDropProps<TEvent, TResource> &
  PlannerCustomProps

type TimeZoneAgnosticCalendarCustomProps = {
  timeZoneId?: string
  calendar: React.FunctionComponent<PlannerProps<BaseEvent, Resource>>
}

export type TimeZoneAgnosticCalendarProps = PlannerProps<BaseEvent, Resource> &
  TimeZoneAgnosticCalendarCustomProps

export type GapInterval = { start: Date; end: Date }

export type RangePeriod = { startDate: string; endDate: string }
export type RangeDatePeriod = { startDate: Date; endDate: Date }
export type RangeChangeProp = Date[] | { start: Date | string; end: Date | string }

export type OnEventDropProps<TEvent extends object = BaseEvent> = {
  event: TEvent
  start: stringOrDate
  end: stringOrDate
  isAllDay: boolean
  resourceId?: string
}

export type OnEventResizeProps<TEvent extends object = BaseEvent> = {
  event: TEvent
  start: stringOrDate
  end: stringOrDate
  isAllDay: boolean
  resourceId?: string
}

export type OnSelectSlotProps = {
  start: stringOrDate
  end: stringOrDate
  slots: Date[] | string[]
  action: 'select' | 'click' | 'doubleClick'
  resourceId?: string
}

export type OnDragStartProps<TEvent extends object = BaseEvent> = {
  event: TEvent
  action: 'resize' | 'move'
  direction: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT'
}

export type OnSelectEventProps<TEvent extends object = BaseEvent> = (
  event: TEvent,
  e: React.SyntheticEvent<HTMLElement>
) => void

export type OnRangeChangeProps = (
  range: Date[] | { start: stringOrDate; end: stringOrDate },
  view: View | undefined
) => void
